import { ClipboardListIcon, Paragraph, TextEditor } from '@quickmd-llc/design-system'
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import './styles.scss'
import '@quickmd-llc/design-system/components/text-editor/TextEditor.css'
import { useDailyVisit } from 'pages/WaitingRoom/daily/hooks/useDailyVisit'
import { useQueryClient } from '@tanstack/react-query'
import {
  usePostVisitsVisitIdPatientinstructions,
  useDeleteVisitsVisitIdPatientinstructions,
} from 'api/generated/visit/visit'
import type {
  VisitDoctorApiOutContract,
  VisitMedicalRecordApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import {
  getGetVisitsVisitIdV2QueryKey,
  useGetVisitsVisitIdV2,
} from 'api/generated/visit-controller-v2/visit-controller-v2'
import { removeAllTags } from '../utils'

export type VisitInstructionsViewRef = {
  saveInstructions: () => Promise<void>
}

export const VisitInstructionsView = React.forwardRef<VisitInstructionsViewRef>((_props, ref) => {
  const textEditorRef = useRef(null)
  const { patientWithVisitInfo } = useDailyVisit()
  const { data: visitDetails } = useGetVisitsVisitIdV2(
    patientWithVisitInfo?.VisitId || '',
    { includeMostRecentMatchingVisit: true },
    {
      query: { enabled: false },
    },
  )
  const currentInstructions =
    Array.isArray(visitDetails?.PatientInstructions) ?
      (visitDetails?.PatientInstructions.find((n) => !n.IsAddendum)?.Message ?? '')
    : ''
  const savedInstructions = useRef<string>(currentInstructions || '')
  const queryClient = useQueryClient()
  const { mutateAsync: saveInstructionsMutation } = usePostVisitsVisitIdPatientinstructions()
  const { mutateAsync: deleteInstructionsMutation } = useDeleteVisitsVisitIdPatientinstructions()
  const [instructions, setInstructions] = useState<string>(currentInstructions || '')
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const initialInstructionsRef = useRef<string>(currentInstructions || '')

  useEffect(() => {
    const initial = initialInstructionsRef.current || ''
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    if (textEditorRef.current && typeof textEditorRef.current.onSetValue === 'function') {
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      textEditorRef.current.onSetValue(initial || '')
    }
    setInstructions(initial || '')
    // Blur the editor after Quill finishes initializing so it doesn't steal focus when the popup opens
    const frame = requestAnimationFrame(() => {
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      const quill = textEditorRef.current?.getEditorInstance?.()
      quill?.blur()
    })
    return () => cancelAnimationFrame(frame)
  }, [])

  const onChangeText = (text: string) => {
    setInstructions(text)
  }

  const onSaveInstructions = useCallback(async () => {
    if (!patientWithVisitInfo?.VisitId) return
    if (instructions === savedInstructions.current) return
    try {
      setIsSaving(true)
      const isEmpty = !instructions?.trim() || removeAllTags(instructions).trim().length === 0
      if (isEmpty) {
        savedInstructions.current = ''
        await deleteInstructionsMutation({ visitId: patientWithVisitInfo.VisitId })
        queryClient.setQueryData<VisitDoctorApiOutContract | undefined>(
          getGetVisitsVisitIdV2QueryKey(patientWithVisitInfo.VisitId, {
            includeMostRecentMatchingVisit: true,
          }),
          (prev) => (prev ? { ...prev, PatientInstructions: [] } : prev),
        )
      } else {
        await saveInstructionsMutation({
          visitId: patientWithVisitInfo.VisitId,
          data: { Message: instructions },
        })
        savedInstructions.current = instructions
        const nowIso = new Date().toISOString()
        queryClient.setQueryData<VisitDoctorApiOutContract | undefined>(
          getGetVisitsVisitIdV2QueryKey(patientWithVisitInfo.VisitId, {
            includeMostRecentMatchingVisit: true,
          }),
          (prev) => {
            if (!prev) return prev
            const addenda =
              Array.isArray(prev.PatientInstructions) ? prev.PatientInstructions.filter((n) => !!n.IsAddendum) : []
            const existingMain =
              Array.isArray(prev.PatientInstructions) ? prev.PatientInstructions.find((n) => !n.IsAddendum) : undefined
            const nextMain: VisitMedicalRecordApiOutContract =
              existingMain ?
                { ...existingMain, Message: instructions, DateTime: nowIso, IsAddendum: false }
              : { Message: instructions, DateTime: nowIso, IsAddendum: false }
            return { ...prev, PatientInstructions: [...addenda, nextMain] }
          },
        )
      }
    } catch (error) {
      console.error('Failed to save/delete instructions', error)
    } finally {
      setIsSaving(false)
    }
  }, [instructions, patientWithVisitInfo?.VisitId, queryClient, saveInstructionsMutation, deleteInstructionsMutation])

  useImperativeHandle(ref, () => ({ saveInstructions: onSaveInstructions }), [onSaveInstructions])

  return (
    <div className='relative flex h-full w-full flex-col overflow-auto bg-white p-sp-2'>
      <div className='flex h-sp-4 items-center justify-between gap-sp-1'>
        <div className='flex items-center gap-sp-0.5'>
          <ClipboardListIcon />
          <Paragraph className='font-semibold text-text-dark'>Patient instructions</Paragraph>
        </div>
      </div>
      <div className='visit-editor-view-container'>
        <TextEditor
          enableHTML
          floatingToolbar
          placeHolderText='Start typing...'
          containerAdditionalClassName='visit-view-text-editor'
          setText={onChangeText}
          ref={textEditorRef}
          saving={isSaving}
          onBlur={onSaveInstructions}
        />
      </div>
    </div>
  )
})

VisitInstructionsView.displayName = 'VisitInstructionsView'
